import { ICurrentUser } from "@/interfaces/iCurrentUser";
import router from "@/router";
import store from "@/store";
import { computed } from "vue";
import { Actions } from "@/store/enums/StoreEnums";

const ID_TOKEN_KEY = "id_token" as string;
const CURRENT_USER = "current_user" as string;
const INACTIVITY_THRESHOLD = 30 * 60 * 1000; // 30 minutes in milliseconds

let inactivityTimer: NodeJS.Timeout | null = null;

// Function to reset the inactivity timer
const resetInactivityTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  inactivityTimer = setTimeout(async () => {
    // Automatically logout user after inactivity threshold
    await store.dispatch(Actions.LOGOUT);
    await logout();
    // setTimeout(() => {
    router.push("/sign-in");
    // }, 5000);
  }, INACTIVITY_THRESHOLD);
};

// Event listeners to track user activity
const setupActivityListeners = () => {
  const activityEvents = [
    "mousemove",
    "mousedown",
    "keydown",
    "touchstart",
    "mouseup",
    "mouseover",
    "mouseout",
    "click",
    "scroll",
    "resize",
    "focus",
    "blur",
  ];
  activityEvents.forEach((event) => {
    document.addEventListener(event, resetInactivityTimer);
  });
};

// Function to logout user
const logout = (): void => {
  destroyToken();
  removeCurrentUser();
  // Additional logout logic (e.g., redirect to login page)
};

// Initialize activity tracking on app start
setupActivityListeners();

/**
 * @returns Current user object
 */
export const getCurrentUser = (): ICurrentUser => {
  resetInactivityTimer(); // Reset inactivity timer on each call to getCurrentUser

  // Retrieve current user from Vuex store
  let currentUser = computed(() => {
    return store.getters.currentUser;
  }).value;

  // If current user is not available in Vuex store, retrieve from localStorage
  if (!currentUser || !currentUser.webId) {
    const storedUser = window.localStorage.getItem(CURRENT_USER);
    if (storedUser) {
      currentUser = JSON.parse(storedUser) as ICurrentUser;
    }
  }

  return currentUser;
};

/**
 * @description Save current user to localStorage
 * @param user Current user object
 */
export const saveCurrentUser = (user: ICurrentUser): void => {
  window.localStorage.setItem(CURRENT_USER, JSON.stringify(user));
};

/**
 * @description Remove current user from localStorage
 */
export const removeCurrentUser = (): void => {
  window.localStorage.removeItem(CURRENT_USER);
};

/**
 * @description Get token from localStorage
 * @returns Token string or null if not found
 */
export const getToken = (): string | null => {
  return window.localStorage.getItem(ID_TOKEN_KEY);
};

/**
 * @description Save token to localStorage
 * @param token Token string or null
 */
export const saveToken = (token: string | null): void => {
  window.localStorage.setItem(ID_TOKEN_KEY, token || "");
};

/**
 * @description Remove token from localStorage
 */
export const destroyToken = (): void => {
  window.localStorage.removeItem(ID_TOKEN_KEY);
};

export default {
  getToken,
  saveToken,
  destroyToken,
  getCurrentUser,
  saveCurrentUser,
  removeCurrentUser,
};
